import { InfoTip } from "@/components/ui/info-tip";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, Download, FileDown, Info, Loader2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import {
  activeFilterCount,
  fetchReportRows,
  fetchReportSummary,
  formatMinutes,
  lastDays,
  reportCsvHref,
  reportPdfHref,
  toSearchParams,
  type ReportBreakdown,
  type ReportQuery,
  type ReportRow,
  type ReportSummary,
} from "@/lib/reports";
import {
  ClearFilters,
  MultiSelectFilter,
  RangePresets,
  type ReportFilterOption,
} from "@/components/reports/report-filters";
import {
  ReportBreakdownChart,
  ReportRatingsChart,
  ReportVolumeChart,
} from "@/components/reports/report-charts";
import { ArrivalHeatmap } from "@/components/reports/arrival-heatmap";
import { ReportTable } from "@/components/reports/report-table";
import { Feature, Permission, useSession, useZone } from "@/components/session-provider";
import { Checkbox } from "@/components/ui/checkbox";

export const Route = createFileRoute("/reports/request-activity")({
  component: ReportsPage,
});

/**
 * The period the report opens on.
 *
 * ⚠️ Must stay in step with `ReportRequestBuilder.DefaultRangeDays`. The server applies its own
 * default when a request carries no range — which is what a bare API call and an export link
 * built by hand both do — so if the two disagree, a CSV taken without touching the filters
 * covers a different period from the screen it was taken from.
 */
const DEFAULT_DAYS = 7;
const PAGE_SIZE = 25;

/**
 * The reports screen (D-67).
 *
 * ⚠️ **The chart and the table always describe the same set.** Both requests carry the
 * identical query object, built once by `toSearchParams`, and the server answers both from one
 * pinned base query. That is the entire point of this screen: a figure you can click into and
 * find the requests behind it, rather than two numbers on one page that need reconciling.
 *
 * ⚠️ **It runs on the reader's real clearance.** Unlike `/analytics`, this surface is outside
 * the D-65 classification elevation, because it returns ticket rows. The visible cost is that a
 * reader's totals here can be lower than the same reader's totals on the analytics screen; the
 * page says which access produced them rather than leaving that to be assumed.
 *
 * ⚠️ **Everything on screen is rendering.** The tier, the pickers, the tabs — the server pins
 * the rows, refuses an assignee filter from the agent tier, and withholds the person-shaped
 * breakdowns regardless of what this bundle believes. Forcing a URL yields an empty page, not
 * somebody else's requests.
 */
function ReportsPage() {
  const { can, hasFeature } = useSession();

  // ⚠️ The zone the SERVER resolved for this reader, which is also the one the summary reports
  // its day buckets in. The range below is a calendar-day window, so cutting it on the
  // browser's midnight instead put the window on a different day from the heading above it.
  const zone = useZone();

  // ⚠️ URL state, so a link shares the QUESTION rather than the answer. The recipient's own
  // tier, query filter and RLS decide what comes back — which is what makes a report link safe
  // to paste into a channel, and why nothing here is ever a cached result (D-52).
  const [query, setQuery] = useState<ReportQuery>(() => readQueryFromUrl(zone));
  const [days, setDays] = useState<number | null>(DEFAULT_DAYS);

  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [rows, setRows] = useState<readonly ReportRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [rowsError, setRowsError] = useState<string | null>(null);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [showParticipants, setShowParticipants] = useState(false);

  /**
   * The filter options, captured from the FIRST load and kept (D-58).
   *
   * ⚠️ Rebuilding them from each filtered response would make every selection remove the
   * others — the filter bar that traps you in whatever you picked first. It also means the
   * options describe the reader's whole readable set rather than the slice currently on screen.
   *
   * ⚠️ STATE, not a ref, and that distinction was a real defect on the way here. A ref does not
   * re-render when it is assigned, so the options were computed once against `null` and the
   * dropdowns stayed permanently empty — a filter bar that renders and filters nothing, which
   * looks like a screen with no filters rather than like a bug.
   */
  const [baseline, setBaseline] = useState<ReportSummary | null>(null);

  const serialised = toSearchParams(query).toString();

  // Keep the address bar in step, without pushing a history entry per keystroke of filtering.
  useEffect(() => {
    const url = serialised ? `${window.location.pathname}?${serialised}` : window.location.pathname;
    window.history.replaceState(null, "", url);
  }, [serialised]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    // ⚠️ Cleared, not left on screen. Stale figures beside a fresh range label look exactly
    // like current ones, and nothing about a chart says it is describing last month.
    setSummary(null);
    setSummaryError(null);
    setSummaryLoading(true);

    void (async () => {
      try {
        const result = await fetchReportSummary(query, controller.signal);
        if (cancelled) return;

        // First load only. Captured before any narrowing, so the options stay stable as the
        // reader filters.
        setBaseline((current) => current ?? result);
        setSummary(result);
        setSummaryLoading(false);
      } catch (e) {
        if (cancelled || controller.signal.aborted) return;

        setSummaryError(
          e instanceof ApiError
            ? e.message
            : "These figures could not be loaded. Please try again.",
        );
        setSummaryLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [query]);

  // Rows load separately, so a slow or failing table does not take the figures with it.
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    setRows([]);
    setCursor(null);
    setHasMore(false);
    setRowsError(null);
    setRowsLoading(true);

    void (async () => {
      try {
        const page = await fetchReportRows(query, null, PAGE_SIZE, controller.signal);
        if (cancelled) return;

        setRows(page.items);
        setCursor(page.nextCursor);
        setHasMore(page.hasMore);
        setRowsLoading(false);
      } catch (e) {
        if (cancelled || controller.signal.aborted) return;

        setRowsError(
          e instanceof ApiError
            ? e.message
            : "These requests could not be loaded. Please try again.",
        );
        setRowsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [query]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;

    setLoadingMore(true);

    try {
      const page = await fetchReportRows(query, cursor, PAGE_SIZE);
      setRows((current) => [...current, ...page.items]);
      setCursor(page.nextCursor);
      setHasMore(page.hasMore);
    } catch (e) {
      setRowsError(e instanceof ApiError ? e.message : "More requests could not be loaded.");
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, loadingMore, query]);

  const options = useMemo(() => buildOptions(baseline), [baseline]);

  const setRange = (nextDays: number) => {
    setDays(nextDays);
    setQuery((current) => ({ ...current, ...lastDays(nextDays, zone) }));
  };

  const narrow = (patch: Partial<ReportQuery>) => setQuery((current) => ({ ...current, ...patch }));

  const clear = () => {
    setQuery((current) => ({ fromUtc: current.fromUtc, toUtc: current.toUtc }));
  };

  const filters = activeFilterCount(query);
  const scope = summary?.scope;

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 p-4 sm:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {/*
            A way back to the catalog, because this is one report among a growing list and a
            page with no route out of it is a page people reach by the nav and leave by the
            browser button.
          */}
          <Link
            to="/reports"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
            All reports
          </Link>

          <h1 className="mt-1 text-xl font-semibold">Request Activity</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Volume, outcomes and the requests behind them.
          </p>
        </div>

        {/*
          ⚠️ AGGREGATES ONLY, and the labels say so. Ticket rows leave through the bulk export,
          which keeps report.export, a written justification, a row cap, a watermark and a
          high-severity audit event. Neither of these is a route around any of that (D-57).
        */}
        <div className="flex items-center gap-2">
          <a
            href={reportCsvHref(query)}
            className="flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-xs font-medium transition-colors hover:bg-muted"
          >
            <Download className="h-3.5 w-3.5" aria-hidden />
            Export figures (CSV)
          </a>

          {/*
            ⚠️ Absent, not disabled, while the flag is off (`BL-029`). A greyed button invites
            "why can't I click this", and the answer — a typeface licensing question — is not
            one the reader can act on. When it is unlocked the button simply appears.
          */}
          {hasFeature(Feature.ReportPdfExport) && (
            <a
              href={reportPdfHref(query)}
              className="flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-xs font-medium transition-colors hover:bg-muted"
            >
              <FileDown className="h-3.5 w-3.5" aria-hidden />
              PDF
            </a>
          )}
        </div>
      </header>

      {/* -------------------------------------------------------------- */}
      {/* Filters. One row, above everything they narrow.                 */}
      {/* -------------------------------------------------------------- */}
      <div className="flex flex-wrap items-center gap-2">
        <RangePresets activeDays={days} onSelect={setRange} disabled={summaryLoading} />

        <MultiSelectFilter
          label="Team"
          options={options.teams}
          selected={query.teamIds ?? []}
          onChange={(teamIds) => narrow({ teamIds })}
        />
        <MultiSelectFilter
          label="Site"
          options={options.sites}
          selected={query.siteIds ?? []}
          onChange={(siteIds) => narrow({ siteIds })}
        />
        <MultiSelectFilter
          label="Category"
          options={options.categories}
          selected={query.categoryIds ?? []}
          onChange={(categoryIds) => narrow({ categoryIds })}
        />
        <MultiSelectFilter
          label="Status"
          options={options.statuses}
          selected={query.statuses ?? []}
          onChange={(statuses) => narrow({ statuses })}
        />

        {/*
          ⚠️ Drawn only where the reader may name somebody (D-68). RENDERING ONLY — an agent who
          forces the parameter into the URL gets a 403 from the server with a message, not their
          colleague's requests. Hidden here so nobody is offered a control that refuses.
        */}
        {scope?.canFilterByPerson && (
          <MultiSelectFilter
            label="Assigned to"
            options={options.assignees}
            selected={query.assigneeIds ?? []}
            onChange={(assigneeIds) => narrow({ assigneeIds })}
          />
        )}

        <MultiSelectFilter
          label="Rating"
          options={options.ratings}
          selected={(query.ratings ?? []).map(String)}
          onChange={(ratings) => narrow({ ratings: ratings.map(Number) })}
        />

        {filters > 0 && <ClearFilters onClear={clear} />}
      </div>

      {/* -------------------------------------------------------------- */}
      {/* What these figures cover. Above the first number, always.       */}
      {/* -------------------------------------------------------------- */}
      {scope && (
        <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <p>
            {/*
              ⚠️ This describes the READER'S ACCESS and never the data. There is no count of what
              is missing here or on the server — "there are 47 requests you cannot see" is the
              enumeration oracle the whole surface avoids.

              ⚠️ An applied filter wins the headline (D-58): somebody who has narrowed to two
              teams and forgotten is the reader most likely to quote a partial figure as a whole
              one.
            */}
            {filters > 0
              ? `Narrowed by ${filters} filter${filters === 1 ? "" : "s"}. `
              : scope.isOwnWorkOnly
                ? "Your own work — requests assigned to you or raised by you. "
                : "Every request you can read. "}
            {summary && (
              <>
                Days shown in {summary.timeZoneId.replace(/_/g, " ")}. Two people with access to
                different queues will see different totals here, and both are right.
              </>
            )}
          </p>
        </div>
      )}

      {summaryError && <ErrorCard message={summaryError} />}

      {/* -------------------------------------------------------------- */}
      {/* Headline.                                                       */}
      {/* -------------------------------------------------------------- */}
      {summary && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Tile
            label="Raised"
            value={summary.headline.raised}
            previous={summary.headline.raisedPrevious}
          />
          <Tile
            label="Of those, resolved"
            value={summary.headline.resolved}
            previous={summary.headline.resolvedPrevious}
          />
          <Tile label="Open now" value={summary.headline.openNow} note="Point in time" />
          <Tile
            label="Unassigned now"
            value={summary.headline.unassignedNow}
            note="Point in time"
          />

          <Tile
            label="Average first response"
            text={formatMinutes(summary.headline.averageFirstResponseMinutes)}
            note="Over requests that have had one"
          />
          <Tile
            label="Average time to resolve"
            text={formatMinutes(summary.headline.averageResolutionMinutes)}
            note="Over requests that were resolved"
          />
        </div>
      )}

      {/* -------------------------------------------------------------- */}
      {/* Charts.                                                         */}
      {/* -------------------------------------------------------------- */}
      {summary && (
        <>
          <Panel
            help="activity-volume"
            title="Raised and resolved"
            subtitle="Requests raised in this period, and how many have since been resolved"
          >
            <ReportVolumeChart points={summary.volume} timeZoneId={summary.timeZoneId} />
          </Panel>

          {/*
            ⚠️ Directly under the volume chart on purpose: that one answers "how much, and when
            across the period", this one answers "and at what times of the week" — the same
            cohort cut a second way, so they are read together.

            ⚠️ Like the volume chart and unlike the breakdowns, its cells do NOT drill through,
            and they are deliberately not drawn to look as though they do. A weekday-and-hour
            filter would have to be computed against a local calendar in SQL, which is where a
            deadline-shaped bug hides plausibly (D-129), and "open six months of Saturday
            afternoons" is not a thing anybody does with this chart — narrowing the date range
            is. The subtitle says what it is instead of hinting at an interaction that is absent.
          */}
          <Panel
            help="activity-arrival"
            title="When requests arrive"
            subtitle="The same requests by day of the week and hour of the day — shaded per occurrence of each hour, so a period holding more of one weekday does not read as busier"
          >
            <ArrivalHeatmap arrival={summary.arrival} timeZoneId={summary.timeZoneId} />
          </Panel>

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="By team" help="activity-team">
              <ReportBreakdownChart
                breakdown={summary.byTeam}
                onSelect={(key) => narrow({ teamIds: [key] })}
              />
            </Panel>

            <Panel title="By site" help="activity-site">
              <ReportBreakdownChart
                breakdown={summary.bySite}
                onSelect={(key) => narrow({ siteIds: [key] })}
              />
            </Panel>

            <Panel title="By category" help="activity-category">
              <ReportBreakdownChart
                breakdown={summary.byCategory}
                onSelect={(key) => narrow({ categoryIds: [key] })}
              />
            </Panel>

            <Panel title="By status" help="activity-status">
              <ReportBreakdownChart breakdown={summary.byStatus} />
            </Panel>

            {/*
              ⚠️ Null means WITHHELD from this tier, not "nobody raised anything" (D-68). The
              panel is absent rather than empty, because an empty "Top requesters" card is a
              claim about the desk and its absence is a claim about the reader's access — and
              only the second one is true.
            */}
            {summary.byRequester && (
              <Panel
                help="activity-requesters"
                title="Top requesters"
                subtitle="Counts only — this card reports demand, never a judgement about a colleague"
              >
                <ReportBreakdownChart breakdown={summary.byRequester} />
              </Panel>
            )}

            {summary.byAssignee && (
              <Panel
                title="By assignee"
                help="activity-assignee"
                subtitle="Includes work nobody holds yet"
              >
                <ReportBreakdownChart
                  breakdown={summary.byAssignee}
                  onSelect={(key) => narrow({ assigneeIds: [key] })}
                />
              </Panel>
            )}

            {/*
              The distribution stays here, because "how was the work in this period rated" is a
              fair question of an activity report and the answer is one chart.

              ⚠️ What is NOT here is a second CSAT. The Satisfaction report owns that
              figure, its denominators and its per-agent cut, and one screen computing a
              satisfaction percentage its own way beside another that computes it differently is
              how two pages come to disagree about one desk (D-53, D-54). Both charts read the
              same cohort — requests raised in this period — so the reader can move between them
              without the question changing underneath.
            */}
            <Panel
              title="Ratings"
              action={
                <Link
                  to="/reports/satisfaction"
                  search={satisfactionSearch(query)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Satisfaction report →
                </Link>
              }
            >
              <ReportRatingsChart ratings={summary.ratings} />
            </Panel>
          </div>
        </>
      )}

      {/* -------------------------------------------------------------- */}
      {/* The requests themselves.                                        */}
      {/* -------------------------------------------------------------- */}
      <Panel
        help="activity-rows"
        title="Requests"
        subtitle="The same set the figures above describe"
        action={
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Checkbox
              checked={showParticipants}
              onChange={(event) => setShowParticipants(event.target.checked)}
            />
            Show who else is involved
          </label>
        }
      >
        {rowsError && <ErrorCard message={rowsError} />}

        {rowsLoading ? (
          <p className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Loading requests…
          </p>
        ) : (
          <>
            <ReportTable rows={rows} showParticipants={showParticipants} />

            {hasMore && (
              <div className="mt-3 flex justify-center">
                <button
                  type="button"
                  onClick={() => void loadMore()}
                  disabled={loadingMore}
                  className="flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-xs font-medium transition-colors hover:bg-muted disabled:opacity-50"
                >
                  {loadingMore && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </Panel>

      {/* -------------------------------------------------------------- */}
      {/* What the figures mean.                                          */}
      {/* -------------------------------------------------------------- */}
      <Definitions ratedBelow={summary?.ratings.followUpBelowStars ?? 4} />

      {/*
        The bulk export lives elsewhere and behind a different permission, and saying so is
        better than a reader assuming the CSV button above is all there is.
      */}
      {can(Permission.ReportExport) && (
        <p className="text-xs text-muted-foreground">
          The CSV above carries the figures on this page. Exporting the requests themselves is a
          separate, justified and watermarked export.
        </p>
      )}
    </div>
  );
}

/**
 * What this report counts, in the reader's own words.
 *
 * ⚠️ This is the half of a "report catalog" that is worth having on day one. D-53 has five
 * quality rates that deliberately do NOT share a denominator, because they are five questions
 * rather than a comparable row — and with more reports coming, a figure whose cohort is not
 * written down beside it is a figure two people will read two ways and argue about in a
 * meeting. Cheaper to state it here than to answer it later.
 *
 * ⚠️ It states DEFINITIONS, never targets. Nobody at ARS has set a threshold for any of these,
 * and a target invented in the browser is an opinion dressed as a measurement (D-53, `BL-025`).
 */
function Definitions({ ratedBelow }: { ratedBelow: number }) {
  return (
    <details className="rounded-xl border border-border bg-card p-4 text-sm">
      <summary className="cursor-pointer text-sm font-semibold">
        How these figures are counted
      </summary>

      <dl className="mt-3 space-y-2 text-xs text-muted-foreground">
        <div>
          <dt className="font-medium text-foreground">The cohort</dt>
          <dd>
            Every figure covers requests <strong>raised</strong> in the period, and follows those
            same requests to whatever happened next. So &ldquo;resolved&rdquo; means &ldquo;raised
            in this period and since resolved&rdquo;, which is not the same as &ldquo;resolved
            during this period&rdquo; — a request raised in March and resolved in April counts in
            March.
          </dd>
        </div>

        <div>
          <dt className="font-medium text-foreground">Open and unassigned</dt>
          <dd>
            Point in time, not period. They describe the queue as it stands right now, out of the
            requests raised in this period.
          </dd>
        </div>

        <div>
          <dt className="font-medium text-foreground">Averages</dt>
          <dd>
            Taken over the requests that have the timestamp, never over all of them. A request
            nobody has replied to yet has no first-response time and is left out rather than counted
            as zero — folding it in would report a desk that answers faster the more people it
            ignores. Nothing to average shows as &ldquo;—&rdquo;.
          </dd>
        </div>

        <div>
          <dt className="font-medium text-foreground">Breakdowns</dt>
          <dd>
            Every cut adds up to the same total, which is printed under each one. A long list folds
            its remainder into &ldquo;Other&rdquo; carrying that count — it is never truncated, so
            the total still holds. There is no small-cell suppression: a row of one describes a
            request you can already open.
          </dd>
        </div>

        <div>
          <dt className="font-medium text-foreground">Ratings</dt>
          <dd>
            The average is shown with the number of ratings behind it, because the two together are
            the fact and the average alone is not. Below {ratedBelow} stars the requester is asked
            what could have gone better.
          </dd>
        </div>

        <div>
          <dt className="font-medium text-foreground">Coverage</dt>
          <dd>
            Every figure is computed over the requests you can already read, under the same rules as
            your queue. Two people with access to different queues will see different totals for the
            same period, and both are right.
          </dd>
        </div>
      </dl>
    </details>
  );
}

// ---------------------------------------------------------------------------

function Panel({
  title,
  subtitle,
  action,
  help,
  children,
}: {
  /** Key into the help registry (`lib/help.ts`). Draws the ⓘ beside the title. */
  help?: string;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="group/card rounded-xl border border-border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-1">
            <h2 className="text-sm font-semibold">{title}</h2>
            {help && <InfoTip id={help} />}
          </div>
          {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/**
 * One headline figure.
 *
 * ⚠️ A change is shown as a COUNT, not a percentage. A group with nothing last period has no
 * honest percentage, and every available answer there — 0%, 100%, infinity — is a claim the
 * data does not support (D-58). A rise is not coloured as bad, either: more requests raised is
 * not a failure, and a red arrow says it is.
 */
function Tile({
  label,
  value,
  text,
  previous,
  note,
}: {
  label: string;
  value?: number;
  text?: string;
  previous?: number;
  note?: string;
}) {
  const delta = value !== undefined && previous !== undefined ? value - previous : null;

  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">
        {text ?? value?.toLocaleString() ?? "—"}
      </p>
      {delta !== null && (
        <p className="mt-0.5 text-xs text-muted-foreground">
          {delta === 0 ? "No change" : `${delta > 0 ? "+" : ""}${delta.toLocaleString()}`} on the
          previous period
        </p>
      )}
      {note && <p className="mt-0.5 text-xs text-muted-foreground">{note}</p>}
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
      {message}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface Options {
  teams: ReportFilterOption[];
  sites: ReportFilterOption[];
  categories: ReportFilterOption[];
  statuses: ReportFilterOption[];
  assignees: ReportFilterOption[];
  ratings: ReportFilterOption[];
}

/**
 * The filter options, from the reader's own unfiltered breakdown (D-58).
 *
 * ⚠️ A row with no key is skipped — "Other" stands for several groups and has nothing to send
 * as a filter. The "No site" row is the exception the server encodes as the literal `none`,
 * because D-50 places unmatched people nowhere and that set has to be selectable.
 */
function buildOptions(baseline: ReportSummary | null): Options {
  const from = (breakdown: ReportBreakdown | null | undefined): ReportFilterOption[] =>
    (breakdown?.rows ?? [])
      .filter((row) => row.key !== null)
      .map((row) => ({ id: row.key!, label: row.label, count: row.count }));

  const sites = (baseline?.bySite.rows ?? [])
    .filter((row) => row.key !== null || row.label === "No site")
    .map((row) => ({ id: row.key ?? "none", label: row.label, count: row.count }));

  const statuses = (baseline?.byStatus.rows ?? []).map((row) => ({
    id: row.label,
    label: row.label,
    count: row.count,
  }));

  const ratings = (baseline?.ratings.distribution ?? []).map((count, index) => ({
    id: String(index + 1),
    label: `${index + 1} star${index === 0 ? "" : "s"}`,
    count,
  }));

  return {
    teams: from(baseline?.byTeam),
    sites,
    categories: from(baseline?.byCategory),
    statuses,
    assignees: from(baseline?.byAssignee),
    ratings,
  };
}

/**
 * The filters already in the address bar, so a shared link opens the same question.
 *
 * ⚠️ Takes the reader's zone because the DEFAULT range is a calendar-day window and a calendar
 * day is only a pair of instants once a zone is known. An explicit range in the URL is already
 * a pair of instants and is used as sent — which is what makes a shared link mean the same
 * window to a colleague in another timezone rather than silently re-anchoring to theirs.
 */
/**
 * The filters the satisfaction report can also express, so following the link keeps the
 * question rather than resetting it.
 *
 * ⚠️ Only the filters BOTH screens have. Carrying a status or a rating selection across
 * would land the reader on a screen that silently ignores it — a link that appears to preserve
 * a filter and does not is worse than one that plainly starts fresh, because the heading would
 * then describe a narrower question than the figures answer.
 */
function satisfactionSearch(query: ReportQuery): Record<string, string | string[]> {
  const search: Record<string, string | string[]> = {};

  if (query.fromUtc) search.raisedFromUtc = query.fromUtc;
  if (query.toUtc) search.raisedToUtc = query.toUtc;
  if (query.teamIds?.length) search.teamIds = [...query.teamIds];
  if (query.siteIds?.length) search.siteIds = [...query.siteIds];
  if (query.categoryIds?.length) search.categoryIds = [...query.categoryIds];

  return search;
}

function readQueryFromUrl(zone: string): ReportQuery {
  const params = new URLSearchParams(window.location.search);

  const many = (key: string): string[] | undefined => {
    const values = params.getAll(key);
    return values.length > 0 ? values : undefined;
  };

  const range = lastDays(DEFAULT_DAYS, zone);

  return {
    fromUtc: params.get("raisedFromUtc") ?? range.fromUtc,
    toUtc: params.get("raisedToUtc") ?? range.toUtc,
    teamIds: many("teamIds"),
    siteIds: many("siteIds"),
    categoryIds: many("categoryIds"),
    sourceIds: many("sourceIds"),
    assigneeIds: many("assigneeIds"),
    statuses: many("statuses"),
    ratings: many("ratings")?.map(Number),
    minimumPriority: params.get("minimumPriority") ?? undefined,
  };
}
